import {
  conversationLimitCutoff,
  effectiveConversationLimit,
  isOverConversationLimit,
  isWithinConversationLimitGrace,
} from "../../supabase/functions/_shared/conversation-limit";
import { paywallDecision, type CurrentBillingAccess } from "@/lib/paywall-access";
import { isBillingLockWaived } from "@/lib/billing-lock-waiver";
import { isOverWithheldCap } from "@/lib/free-quota";
import { isRecargasPlan, zeroQuotaIsCap } from "@/lib/credits-plan";
import { canBuyExtraPacks } from "@/lib/extra-packs";
import { CREDITS_TOPUP_PATH } from "@/lib/credits";
import type { SubscriptionEnd } from "@/lib/subscription-end";

export type AccountStandingKind = "ok" | "grace" | "paused" | "closed";

export type ClosedReason = "terminated" | "manual_block";

export type GraceReason = "payment_overdue" | "payment_failed" | "over_limit";

export type PausedReason =
  | "payment_overdue"
  | "payment_failed"
  | "subscription_suspended"
  | "authorization_lost"
  | "prepaid_expired"
  | "ai_inactive"
  | "credits_exhausted"
  | "over_limit";

export type StandingAction = "pay" | "plan" | "top_up" | "support";

export type AccountStanding =
  | { kind: "ok" }
  | GraceStanding
  | {
    kind: "paused";
    reason: PausedReason;
    pausedSince: Date | null;
    amountOwed: number | null;
    action: StandingAction;
    owedSince?: Date | null;
    owedCycles?: number | null;
  }
  | { kind: "closed"; reason: ClosedReason };

export interface GraceStanding {
  kind: "grace";
  reason: GraceReason;
  aiAnswersUntil: Date | null;
  amountOwed: number | null;
  conversationLimit: number | null;
  action: StandingAction;
}

export interface AccountStandingOrg {
  plan: string | null;
  conversations_used: number | null;
  conversations_limit: number | null;
  conversations_extra?: number | null;
  overage_accepted_at?: string | null;
  limit_exceeded_at: string | null;
  free_quota_withheld?: boolean | null;
  billing_exempt?: boolean | null;
  billing_override_until?: string | null;
  billing_no_charge?: boolean | null;
  ai_active?: boolean | null;
  manual_block?: boolean | null;
  per_use_authorization_lost_at?: string | null;
}

export interface AccountStandingBilling {
  loading: boolean;
  isCustomPlan: boolean;
  billingMode: string | null;
  billingState: string;
  graceDeadline: Date | null;
  subscriptionEnd?: SubscriptionEnd | null;
}

export interface AccountStandingCredits {
  loading: boolean;
  covers: boolean;
  exhausted: boolean;
  owedCop?: number | null;
}

export interface AccountStandingOwed {
  access?: number | null;
  overview?: number | null;
  payableCycle?: number | null;
  estimated?: number | null;
}

export interface AccountStandingInput {
  brandId: string;
  subscriptionStatus: string | null;
  organizationId: string | null;
  access: CurrentBillingAccess | null;
  accessLoading: boolean;
  org: AccountStandingOrg | null;
  billing: AccountStandingBilling | null;
  credits: AccountStandingCredits | null;
  owed?: AccountStandingOwed;
  now?: Date;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export type PausedStanding = Extract<AccountStanding, { kind: "paused" }>;

export const ACCOUNT_PAYMENTS_PATH = "/app/settings/account#pagos";
export const ACCOUNT_PLAN_PATH = "/app/settings/account#plan";

export function standingActionPath(action: Exclude<StandingAction, "support">): string {
  if (action === "top_up") return CREDITS_TOPUP_PATH;
  if (action === "plan") return ACCOUNT_PLAN_PATH;
  return ACCOUNT_PAYMENTS_PATH;
}

function calendarDay(date: Date | null): Date | null {
  if (!date || Number.isNaN(date.getTime())) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12));
}

function parseInstant(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function firstPositive(...values: Array<number | null | undefined>): number | null {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  }
  return null;
}

function overLimitAction(org: AccountStandingOrg, billingMode: string | null): StandingAction {
  if (isRecargasPlan(org.plan)) return "top_up";
  if (org.free_quota_withheld === true) return "plan";
  return canBuyExtraPacks(org.plan) && billingMode !== "per_use" ? "top_up" : "plan";
}

function paused(reason: PausedReason, action: StandingAction, pausedSince: Date | null, amountOwed: number | null): AccountStanding {
  return { kind: "paused", reason, action, pausedSince, amountOwed };
}

function overduePause(access: CurrentBillingAccess | null, amountOwed: number | null): AccountStanding {
  const graceUntil = parseInstant(access?.grace_until);
  const pausedSince = parseInstant(access?.suspended_at) ?? (graceUntil ? new Date(graceUntil.getTime() + DAY_MS) : null);
  const owedSince = parseInstant(access?.oldest_due_date);
  const owedCycles = access?.due_cycles != null && access.due_cycles > 0 ? access.due_cycles : null;
  return {
    kind: "paused",
    reason: "payment_overdue",
    action: "pay",
    pausedSince,
    amountOwed,
    ...(owedSince ? { owedSince } : {}),
    ...(owedCycles !== null ? { owedCycles } : {}),
  };
}

export function accountStanding(input: AccountStandingInput): AccountStanding {
  const { org, billing, credits, owed = {} } = input;
  const now = input.now ?? new Date();

  const decision = paywallDecision({
    subscriptionStatus: input.subscriptionStatus,
    brandId: input.brandId,
    organizationId: input.organizationId,
    access: input.access,
    accessLoading: input.accessLoading,
  });

  if (decision.kind === "locked" && decision.reason === "terminated") return { kind: "closed", reason: "terminated" };
  if (decision.kind === "locked" && decision.reason === "manual_block") return { kind: "closed", reason: "manual_block" };
  if (org?.manual_block === true) return { kind: "closed", reason: "manual_block" };

  const accessOwed = firstPositive(owed.access, owed.overview);

  if (decision.kind === "locked") {
    switch (decision.reason) {
      case "overdue":
        return overduePause(input.access, accessOwed);
      case "authorization_lost":
        return paused("authorization_lost", "support", parseInstant(org?.per_use_authorization_lost_at), accessOwed);
      case "prepaid_expired":
        return paused("prepaid_expired", "plan", null, accessOwed);
      case "ai_inactive":
        return paused("ai_inactive", "pay", null, accessOwed);
      default:
        return paused("subscription_suspended", "pay", null, accessOwed);
    }
  }

  if (org?.per_use_authorization_lost_at) {
    return paused("authorization_lost", "support", parseInstant(org.per_use_authorization_lost_at), accessOwed);
  }
  const aiOff = org?.ai_active === false;
  const aiInactive = () => paused("ai_inactive", accessOwed !== null ? "pay" : "plan", null, accessOwed);
  if (input.brandId !== "flow") return aiOff ? aiInactive() : { kind: "ok" };
  const settle = (candidate: GraceStanding): AccountStanding =>
    aiOff ? paused(candidate.reason, candidate.action, null, candidate.amountOwed) : candidate;

  const billingMode = billing?.billingMode ?? null;
  const cycleBilled = !!billing && !billing.loading && (billing.isCustomPlan || billingMode === "per_use");
  const cycleWaived = isBillingLockWaived(org, now);
  const cycleOwed = firstPositive(owed.payableCycle, owed.overview);
  const cycleOutstanding = cycleOwed !== null || firstPositive(owed.estimated) !== null;
  let grace: GraceStanding | null = null;

  if (cycleBilled && !cycleWaived && billing) {
    if (billing.billingState === "prepaid-expired" && org?.billing_no_charge !== true) {
      return paused("prepaid_expired", "plan", null, cycleOwed);
    }
    const deadline = calendarDay(billing.graceDeadline);
    if (billing.billingState === "overdue-expired" && cycleOutstanding) {
      return paused("payment_overdue", "pay", deadline ? new Date(deadline.getTime() + DAY_MS) : null, cycleOwed);
    }
    if (billing.billingState === "overdue-grace" && cycleOutstanding) {
      grace = { kind: "grace", reason: "payment_overdue", aiAnswersUntil: deadline, amountOwed: cycleOwed, conversationLimit: null, action: "pay" };
    }
  }

  if (org && credits && !credits.loading && !credits.covers) {
    const zeroCap = zeroQuotaIsCap(org);
    const overLimit = zeroCap ? isOverWithheldCap(org) : isOverConversationLimit(org);
    const creditsOwed = firstPositive(credits.owedCop);
    if (credits.exhausted && isRecargasPlan(org.plan) && creditsOwed !== null) {
      return paused("credits_exhausted", "top_up", null, creditsOwed);
    }
    if (overLimit) {
      const action = overLimitAction(org, billingMode);
      if (credits.exhausted) return paused("credits_exhausted", "top_up", null, creditsOwed);
      if (zeroCap) return paused("over_limit", action, null, null);
      const cutoff = conversationLimitCutoff(org.limit_exceeded_at);
      if (cutoff && !isWithinConversationLimitGrace(org.limit_exceeded_at, now)) {
        return paused("over_limit", action, cutoff, null);
      }
      grace ??= {
        kind: "grace",
        reason: "over_limit",
        aiAnswersUntil: cutoff,
        amountOwed: null,
        conversationLimit: effectiveConversationLimit(org),
        action,
      };
    }
  }

  if (grace) return settle(grace);

  const subscriptionEnd = billing?.subscriptionEnd;
  if (billing && !billing.loading && subscriptionEnd?.kind === "payment_failed" && !cycleBilled) {
    const suspendOn = parseInstant(subscriptionEnd.suspendOn);
    const failedOwed = firstPositive(owed.payableCycle, owed.overview);
    if (suspendOn && suspendOn.getTime() <= now.getTime()) return paused("payment_failed", "pay", suspendOn, failedOwed);
    return settle({ kind: "grace", reason: "payment_failed", aiAnswersUntil: suspendOn, amountOwed: failedOwed, conversationLimit: null, action: "pay" });
  }

  return aiOff ? aiInactive() : { kind: "ok" };
}

const AGENCY_LOCKING_REASONS: ReadonlySet<string> = new Set(["payment_overdue", "prepaid_expired", "credits_exhausted", "over_limit"]);

export function standingLocksAgencyWorkspace(standing: AccountStanding): boolean {
  return (standing.kind === "grace" || standing.kind === "paused") && AGENCY_LOCKING_REASONS.has(standing.reason);
}
